"use client";

import { useEffect, useRef, useState, ReactNode } from "react";
import { motion, HTMLMotionProps } from "motion/react";

interface TimelineContentProps extends HTMLMotionProps<"div"> {
  children: ReactNode;
  animationNum: number;
  timelineRef: React.RefObject<HTMLDivElement>;
  customVariants?: Record<string, unknown>;
  as?: keyof JSX.IntrinsicElements;
}

export function TimelineContent({
  children,
  timelineRef,
  customVariants,
  as = "div",
  ...props
}: TimelineContentProps) {
  const [isVisible, setIsVisible] = useState(false);
  const elementRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px",
      },
    );

    if (elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (elementRef.current) {
        observer.unobserve(elementRef.current);
      }
    };
  }, []);

  const Component = (motion as any)[as] || motion.div;

  return (
    <Component
      ref={elementRef as never}
      initial="hidden"
      animate={isVisible ? "visible" : "hidden"}
      variants={
        customVariants || {
          visible: { opacity: 1, y: 0 },
          hidden: { opacity: 0, y: 20 },
        }
      }
      {...props}
    >
      {children}
    </Component>
  );
}
